import React from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { V2Card } from '@uhg-abyss/web/ui/Card';
import { V2Button } from '@uhg-abyss/web/ui/Button';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';

const SectionStack = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

const SectionHeader = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '24px',
});

const Label = styled('p', {
  margin: 0,
  fontWeight: 700,
});

const Value = styled('p', {
  margin: 0,
  wordBreak: 'break-all',
  whiteSpace: 'normal',
});

const AddressValue = styled(Value, {
  whiteSpace: 'pre-line',
});

const DetailGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});

const TwoColumnRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(240px, 340px))',
  gap: 'clamp(20px, 16vw, 360px)',
  justifyContent: 'start',
});

const LocationGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
});

const getUploadedFileName = (fileValue) => {
  if (!fileValue) return 'No file uploaded';
  if (Array.isArray(fileValue)) {
    const file = fileValue[0];
    return file?.name || 'No file name';
  }
  if (typeof fileValue === 'string') return fileValue;
  if (fileValue?.name) return fileValue.name;
  return 'No file name';
};

const formatAddress = (address) => {
  if (!address) return 'No address provided';
  const lineOne = [address.streetLine1, address.streetLine2]
    .filter(Boolean)
    .join(', ');
  const lineTwo = [address.city, address.state, address.zipCode]
    .filter(Boolean)
    .join(', ');

  return [lineOne, lineTwo].filter(Boolean).join('\n') || 'No address added';
};

export const LocationInformationCard = ({ onEdit, isPrinting }) => {
  const abyssForm = useFormV2();
  const addressInfo = abyssForm.getValues('address') || {};

  return (
    <V2Card>
      <SectionHeader>
        <h4>Location information</h4>
        {!isPrinting && (
          <V2Button
            variant="outline"
            size="$sm"
            onClick={() => onEdit(3)}
            icon={{
              icon: 'edit',
              position: 'leading',
              variant: 'outlined',
            }}
          >
            Edit
          </V2Button>
        )}
      </SectionHeader>
      <SectionStack>
        <LocationGroup>
          <h5 style={{ margin: 0 }}>Servicing address</h5>
          <TwoColumnRow>
            <DetailGroup>
              <Label>Primary servicing address</Label>
              <AddressValue>
                {formatAddress(addressInfo.primaryServicing)}
              </AddressValue>
            </DetailGroup>
            <DetailGroup>
              <Label>Proof of address</Label>
              <Value>
                {getUploadedFileName(addressInfo.primaryServicingProof)}
              </Value>
            </DetailGroup>
          </TwoColumnRow>
        </LocationGroup>

        <LocationGroup>
          <h5 style={{ margin: 0 }}>Billing</h5>
          <TwoColumnRow>
            <DetailGroup>
              <Label>Primary billing address</Label>
              <AddressValue>
                {formatAddress(addressInfo.primaryBilling)}
              </AddressValue>
            </DetailGroup>
            <DetailGroup>
              <Label>Proof of address</Label>
              <Value>
                {getUploadedFileName(addressInfo.primaryBillingProof)}
              </Value>
            </DetailGroup>
          </TwoColumnRow>
        </LocationGroup>

        <LocationGroup>
          <h5 style={{ margin: 0 }}>Remittance</h5>
          <TwoColumnRow>
            <DetailGroup>
              <Label>Primary remittance address</Label>
              <AddressValue>
                {formatAddress(addressInfo.primaryRemittance)}
              </AddressValue>
            </DetailGroup>
            <DetailGroup>
              <Label>Proof of address</Label>
              <Value>
                {getUploadedFileName(addressInfo.primaryRemittanceProof)}
              </Value>
            </DetailGroup>
          </TwoColumnRow>
        </LocationGroup>

        <LocationGroup>
          <h5 style={{ margin: 0 }}>Secondary servicing address(es)</h5>
          {addressInfo.secondaryServicing?.length > 0 ? (
            addressInfo.secondaryServicing.map((addr, idx, arr) => {
              return (
                <TwoColumnRow key={idx}>
                  <DetailGroup>
                    <Label>
                      Servicing address ({idx + 1} of {arr.length})
                    </Label>
                    <AddressValue>{formatAddress(addr)}</AddressValue>
                  </DetailGroup>
                  <DetailGroup>
                    <Label>Proof of address</Label>
                    <Value>{getUploadedFileName(addr.proof)}</Value>
                  </DetailGroup>
                </TwoColumnRow>
              );
            })
          ) : (
            <Value>No secondary servicing addresses added</Value>
          )}
        </LocationGroup>
      </SectionStack>
    </V2Card>
  );
};
